import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Calendar, Clock, Upload, Music, CheckCircle, AlertCircle, MessageSquare, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import GoogleDrivePreview from '../components/GoogleDrivePreview';
import UpgradePrompt from '../components/UpgradePrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Coaching = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [mixUrl, setMixUrl] = useState('');
  const [mixTitle, setMixTitle] = useState('');
  const [mixNotes, setMixNotes] = useState('');
  const [loading, setLoading] = useState({});
  const [activeTab, setActiveTab] = useState('book');

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email === 'contact@blendonics.com';
  
  // Get effective tier
  const getEffectiveTier = () => {
    if (isAdmin) return 'elite';
    if (user?.membership_status === 'lifetime' && user?.has_course_access) return 'starter';
    if (user?.membership_status === 'active' || user?.membership_status === 'cancelling') {
      return user?.membership_tier || 'free';
    }
    return 'free';
  };
  
  const tier = getEffectiveTier();
  const canAccessCoaching = tier === 'elite' || isAdmin;

  useEffect(() => {
    if (canAccessCoaching) {
      fetchSessions();
      fetchMixes();
    }
  }, [user, canAccessCoaching]);

  // Show upgrade prompt for non-elite users
  if (!canAccessCoaching) {
    return (
      <UpgradePrompt 
        feature="coaching" 
        currentTier={tier} 
        requiredTier="elite"
        title="Elite Coaching"
        description="Get personalized 1:1 coaching sessions, upload your mixes for expert feedback, and accelerate your DJ journey with professional guidance."
      />
    );
  }

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/coaching/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions', error);
    }
  };

  const fetchMixes = async () => {
    try {
      const response = await axios.get(`${API}/coaching/mixes`);
      setMixes(response.data);
    } catch (error) {
      console.error('Failed to fetch mixes', error);
    }
  };

  const handleBookSession = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, booking: true });
    try {
      await axios.post(`${API}/coaching/book`, {
        date: bookingDate,
        time: bookingTime,
      });
      toast.success('Coaching session booked!');
      setBookingDate('');
      setBookingTime('');
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    } finally {
      setLoading({ ...loading, booking: false });
    }
  };

  const handleUploadMix = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, upload: true });
    try {
      await axios.post(`${API}/coaching/upload-mix`, {
        mix_url: mixUrl,
        title: mixTitle,
        notes: mixNotes,
      });
      toast.success('Mix uploaded for review!');
      setMixUrl('');
      setMixTitle('');
      setMixNotes('');
      fetchMixes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload mix');
    } finally {
      setLoading({ ...loading, upload: false });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', text: 'Scheduled' },
      completed: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', text: 'Completed' },
      cancelled: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', text: 'Cancelled' },
      pending_review: { bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', text: 'Pending Review' },
      reviewed: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', text: 'Reviewed' },
    };
    const style = styles[status] || styles.scheduled;
    return (
      <span className="text-xs px-2 py-1 rounded-full" style={{ background: style.bg, color: style.color }}>
        {style.text}
      </span>
    );
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen py-12" data-testid="coaching-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold" data-testid="coaching-title">Elite Coaching</h1>
            <p className="text-muted-foreground mt-2">Book 1:1 sessions and get personalized feedback on your mixes</p>
          </div>
          {isAdmin && (
            <span className="text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}>
              Admin Access
            </span>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-8 grid grid-cols-3 gap-2">
            <TabsTrigger value="book">
              <Calendar size={16} className="mr-2" />
              Book Session
            </TabsTrigger>
            <TabsTrigger value="mixes">
              <Music size={16} className="mr-2" />
              My Mixes
            </TabsTrigger>
            <TabsTrigger value="sessions">
              <Clock size={16} className="mr-2" />
              My Sessions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="book">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Book Session Form */}
              <div className="glass-panel rounded-2xl p-6 border" data-testid="book-session-section">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Calendar style={{ color: '#D4AF37' }} size={24} />
                  Book a Session
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Schedule your monthly 1:1 coaching call with DJ Elliott Rock (15-20 minutes)
                </p>
                <form onSubmit={handleBookSession} className="space-y-4">
                  <div>
                    <Label htmlFor="date">Select Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      min={getMinDate()}
                      required
                      className="mt-1"
                      data-testid="booking-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Select Time</Label>
                    <select
                      id="time"
                      value={bookingTime}
                      onChange={(e) => setBookingTime(e.target.value)}
                      required
                      className="w-full mt-1 p-2 rounded-md border border-border bg-background"
                      data-testid="booking-time-input"
                    >
                      <option value="">Choose a time slot</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                      <option value="17:00">5:00 PM</option>
                      <option value="18:00">6:00 PM</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={loading.booking}
                    style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
                    data-testid="book-session-button"
                  >
                    {loading.booking ? 'Booking...' : 'Book Session'}
                  </Button>
                </form>
              </div>

              {/* Upload Mix Form */}
              <div className="glass-panel rounded-2xl p-6 border" data-testid="upload-mix-section">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Upload style={{ color: '#D4AF37' }} size={24} />
                  Upload Mix for Feedback
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Share your mix for personalized feedback from DJ Elliott Rock
                </p>
                <form onSubmit={handleUploadMix} className="space-y-4">
                  <div>
                    <Label htmlFor="mixTitle">Mix Title</Label>
                    <Input
                      id="mixTitle"
                      type="text"
                      value={mixTitle}
                      onChange={(e) => setMixTitle(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="e.g., House Mix January 2025"
                      data-testid="mix-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mixUrl">Mix URL (Google Drive, SoundCloud, Mixcloud)</Label>
                    <Input
                      id="mixUrl"
                      type="url"
                      value={mixUrl}
                      onChange={(e) => setMixUrl(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="https://drive.google.com/file/d/... or SoundCloud/Mixcloud URL"
                      data-testid="mix-url-input"
                    />
                  </div>
                  {mixUrl && (
                    <GoogleDrivePreview url={mixUrl} type="audio" />
                  )}
                  <div>
                    <Label htmlFor="mixNotes">Notes for Feedback (Optional)</Label>
                    <Textarea
                      id="mixNotes"
                      value={mixNotes}
                      onChange={(e) => setMixNotes(e.target.value)}
                      className="mt-1"
                      rows={3}
                      placeholder="What specific areas would you like feedback on? (transitions, track selection, EQ, etc.)"
                      data-testid="mix-notes-textarea"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={loading.upload}
                    style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}
                    data-testid="upload-mix-button"
                  >
                    {loading.upload ? 'Uploading...' : 'Submit for Review'}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="mixes">
            <div data-testid="mixes-list">
              <h2 className="text-2xl font-bold mb-6">Your Uploaded Mixes</h2>
              {mixes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground glass-panel rounded-2xl border">
                  <Music size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No mixes uploaded yet.</p>
                  <p className="text-sm mt-2">Upload your first mix to get personalized feedback!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mixes.map((mix) => (
                    <div key={mix.id} className="glass-panel rounded-xl p-6 border" data-testid={`mix-${mix.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <Music size={20} style={{ color: '#D4AF37' }} />
                            <h3 className="text-lg font-bold">{mix.title}</h3>
                            {getStatusBadge(mix.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Submitted: {new Date(mix.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <a
                          href={mix.mix_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-full hover:bg-white/10 transition-colors"
                          title="Open mix"
                        >
                          <ExternalLink size={18} style={{ color: '#D4AF37' }} />
                        </a>
                      </div>
                      
                      {mix.notes && (
                        <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <p className="text-sm text-muted-foreground">
                            <span className="font-semibold">Your notes:</span> {mix.notes}
                          </p>
                        </div>
                      )}

                      {mix.feedback && (
                        <div className="p-4 rounded-lg border" style={{ background: 'rgba(212, 175, 55, 0.05)', borderColor: 'rgba(212, 175, 55, 0.3)' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare size={16} style={{ color: '#D4AF37' }} />
                            <span className="font-semibold" style={{ color: '#D4AF37' }}>Feedback from DJ Elliott Rock</span>
                          </div>
                          <p className="text-sm">{mix.feedback}</p>
                        </div>
                      )}

                      {mix.status === 'pending_review' && (
                        <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                          <AlertCircle size={16} />
                          <span>Your mix is in the queue for review. You&apos;ll receive feedback soon!</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <div data-testid="sessions-list">
              <h2 className="text-2xl font-bold mb-6">Your Coaching Sessions</h2>
              {sessions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground glass-panel rounded-2xl border">
                  <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No sessions booked yet.</p>
                  <p className="text-sm mt-2">Book your first coaching session to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <div key={session.id} className="glass-panel rounded-xl p-6 border" data-testid={`session-${session.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar size={18} style={{ color: '#D4AF37' }} />
                              <span className="font-semibold">{new Date(session.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={18} style={{ color: '#D4AF37' }} />
                              <span className="font-mono">{session.time}</span>
                            </div>
                          </div>
                          {getStatusBadge(session.status)}
                        </div>
                      </div>
                      {session.notes && (
                        <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <p className="text-sm text-muted-foreground">{session.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Coaching;
